import { useMemo } from "react";

import type { AutomatedSurveyQueueDetailsListDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import type { AutomatedSurveyType } from "../../constants";

export interface ActiveSurveyDetailsProps {
  surveyTitle: string;
  surveyType: AutomatedSurveyType;
  activeSurveys: AutomatedSurveyQueueDetailsListDto[];
  isLoadingActiveSurveys: boolean;
  page: number;
  onPageChange: (page: number) => void;
  totalPages: number;
}

export function ActiveSurveyDetails({
  surveyTitle,
  surveyType,
  activeSurveys,
  isLoadingActiveSurveys,
  page,
  onPageChange,
  totalPages,
}: ActiveSurveyDetailsProps): React.ReactNode {
  const connectedProjects = useLinkedProjects();
  const { t } = useTranslation();
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<AutomatedSurveyQueueDetailsListDto>();

    return [
      helper.accessor("projectName", {
        header: t("page.automated-surveys-active-details.table.project"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("totalResponses", {
        header: t("page.automated-surveys-active-details.table.n-responses"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("numberLeftInQueue", {
        header: t("page.automated-surveys-active-details.table.n-left-in-queue"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("feelingAtHomeAverage", {
        header: t("page.automated-surveys-active-details.table.score.feel-at-home"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("buildingAverage", {
        header: t("page.automated-surveys-active-details.table.score.building"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),

      helper.accessor("communityAverage", {
        header: t("page.automated-surveys-active-details.table.score.community"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("serviceAverage", {
        header: t("page.automated-surveys-active-details.table.score.service"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
    ];
  }, [t]);

  const tableInstance = useResizableTable(TABLE_KEYS.AUTOMATED_SURVEY_QUEUE, {
    columns,
    data: activeSurveys,
    enableSorting: false,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={surveyTitle}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.automated-surveys.title"),
              to: routes.automatedSurveys.list(),
            },
            {
              name: `${surveyTitle} (${
                surveyType === "feelAtHome"
                  ? t("page.automated-surveys.types.feeling-at-home")
                  : t("page.automated-surveys.types.other")
              })`,
            },
          ]}
        />
      }
    >
      <Stack gap="sm">
        <span className="text-caption">{t("page.automated-surveys-active-details.tooltip")}</span>
        {activeSurveys.length || isLoadingActiveSurveys ? (
          <Table
            table={tableInstance}
            getRowHref={(row) => {
              const slug = connectedProjects.data?.find((x) => x.id === row.original.projectId)?.slug;

              return slug ? routes.surveys.fah({ slug }) : "";
            }}
            isLoading={isLoadingActiveSurveys}
          >
            <Pagination count={totalPages} onChange={onPageChange} currentIndex={page} />
          </Table>
        ) : (
          <SectionCard>
            <p>{t("page.automated-surveys-active-details.no-data")}</p>
          </SectionCard>
        )}
      </Stack>
    </DocumentPage>
  );
}
